import { type Store, createLens } from '@frontend/viewer-core';

import type { HistoryStore } from './historyStore';
import type { PointerTracker } from './pointerTracker';
import { isBrushTool } from './toolController';
import type {
  RoleChromeManager,
  DrawingModel,
  DxfSessionState,
  MappingManager,
  MarqueeController,
  MeasureManager,
  ModeManager,
  OverlayManager,
  ToolId,
  ViewerMode,
} from './types';

export const createModeSlice = (
  state: Store<DxfSessionState>,
): Store<ViewerMode> =>
  createLens(
    state,
    (current) => current.mode,
    (current, mode) => ({ ...current, mode }),
  );

type Dependencies = {
  mode: Store<ViewerMode>;
  /**
   * Written directly rather than through `ToolManager.set`, which clears the
   * selection — the one thing a mode change must not do.
   */
  tool: Store<ToolId>;
  mapping: Pick<MappingManager, 'getState' | 'beginSession' | 'endSession'>;
  measure: Pick<MeasureManager, 'clear'>;
  overlays: Pick<OverlayManager, 'set'>;
  marqueeController: Pick<MarqueeController, 'abort'>;
  pointerTracker: Pick<PointerTracker, 'reset'>;
  roleChrome: Pick<RoleChromeManager, 'close'>;
  /**
   * Undo/redo is scoped to a single mapping session, so the stack is emptied at
   * both boundaries — there is nothing to undo back *into* viewing mode, and a
   * fresh session must not inherit the last one's steps.
   */
  history: Pick<HistoryStore, 'reset'>;
  getModel: () => DrawingModel | null;
};

/**
 * Viewing versus mapping.
 *
 * The mode is its own axis rather than another `ToolId` for one load-bearing
 * reason: `ToolManager.set` clears the selection, and the spec requires a
 * selection made while viewing to survive into mapping so it can be given a role
 * immediately. Everything else a tool switch discards — a half-finished
 * measurement, a live marquee, stale pointer feedback — is still discarded here,
 * because none of it means anything under the other mode.
 *
 * Measurement overlays are switched off on the way in but deliberately *not*
 * switched back on when leaving: they are the user's own display preference, and
 * a mode round-trip should not quietly rewrite it.
 */
export const createModeController = ({
  mode,
  tool,
  mapping,
  measure,
  overlays,
  marqueeController,
  pointerTracker,
  roleChrome,
  history,
  getModel,
}: Dependencies): ModeManager => {
  const canEnterMapping = () =>
    Boolean(getModel()) && mapping.getState().lockReason === undefined;

  return {
    getState: mode.getState,
    subscribe: mode.subscribe,
    canEnterMapping,

    set(next) {
      // A redundant call is not a change of intent, and must not cancel a gesture
      // or re-seed a mapping session that is already under way.
      if (mode.getState() === next) {
        return;
      }
      if (next === 'mapping' && !canEnterMapping()) {
        return;
      }

      mode.setState(() => next);

      // Selection survives; everything transient does not.
      measure.clear();
      marqueeController.abort();
      pointerTracker.reset();
      roleChrome.close();

      if (next === 'mapping') {
        // Mapping has no second tool, and the measure tool would otherwise stay
        // active with no way to leave it — its button is not rendered here.
        tool.setState(() => 'select');
        overlays.set('axisDimensions', false);
        overlays.set('optimalBoundingBox', false);
        mapping.beginSession();
        // After seeding, not before: the stack's first entry has to be the state
        // the user is looking at, or the first undo would walk back to an unseeded
        // mapping that mapping mode cannot represent.
        history.reset();
        return;
      }

      mapping.endSession();
      history.reset();
      // Brushes are mapping-only; disarm without `ToolManager.set`, which would
      // clear the selection the spec requires to survive a mode round-trip.
      if (isBrushTool(tool.getState())) {
        tool.setState(() => 'select');
      }
    },
  };
};
